/**
 * Aba Etapas da ficha da frente (#365, D3): as etapas com prazo previsto e real, a situação de
 * cada uma, e as ações de adicionar, iniciar, concluir, corrigir e apagar. "Iniciar" e "Concluir"
 * gravam o real com a data de hoje. Frente encerrada trava adicionar, iniciar e concluir, com o
 * motivo escrito.
 */
import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Alert, Box, Chip, type SxProps, type Theme } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { DataTable, type DataTableColumn } from '../../../components';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { workFrontService } from '../../../services/workFrontService';
import type {
  UpdateWorkFrontStageInput,
  WorkFrontDetail,
  WorkFrontStageItem,
  WorkFrontStageSituation,
} from '../../../types/workFront';
import { useAcao } from '../../../authorization/useVeredito';
import { LockableActionButton } from '../../fire-equipments/components/LockableActionButton';
import { formatIsoDate, todayIso } from '../../fire-equipments/fireDates';
import StageDialog from './StageDialog';

const TOUCH_TARGET = 44;

/** O motivo que acompanha todo controle travado por a frente estar encerrada. */
export const FRONT_CLOSED_REASON = 'Frente encerrada: reabra para alterar';

type Tone = 'success' | 'error' | 'info' | 'default';

const TONE_BY_SITUATION: Record<WorkFrontStageSituation, Tone> = {
  CONCLUIDA: 'success',
  ATRASADA: 'error',
  EM_ANDAMENTO: 'info',
  NAO_INICIADA: 'default',
};

/** Texto e borda no tom escuro da paleta: o `main` fica abaixo de 4,5:1 sobre o branco. */
const readableChipSx =
  (tone: Tone): SxProps<Theme> =>
  (theme) =>
    tone === 'default'
      ? {}
      : {
          color: theme.palette[tone].dark,
          borderColor: theme.palette[tone].dark,
          ...theme.applyStyles('dark', { color: theme.palette[tone].light, borderColor: theme.palette[tone].light }),
        };

const stageSituationLabel = (stage: WorkFrontStageItem): string => {
  switch (stage.situation) {
    case 'CONCLUIDA':
      return 'Concluída';
    case 'ATRASADA':
      return `Atrasada ${stage.lateDays} ${stage.lateDays === 1 ? 'dia' : 'dias'}`;
    case 'EM_ANDAMENTO':
      return 'Em andamento';
    case 'NAO_INICIADA':
      return 'Não iniciada';
  }
};

const period = (start: string | null, end: string | null): string =>
  start || end ? `${start ? formatIsoDate(start) : '—'} a ${end ? formatIsoDate(end) : '—'}` : '—';

export interface StagesTabProps {
  front: WorkFrontDetail;
  closed: boolean;
  /** Recarrega a ficha: as contagens e a situação da frente mudam com a etapa. */
  onChanged: () => Promise<void>;
}

export const StagesTab: React.FC<StagesTabProps> = ({ front, closed, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  // Fica até o diálogo terminar de fechar, para o título não piscar "Adicionar etapa".
  const [editing, setEditing] = useState<WorkFrontStageItem | undefined>(undefined);
  const [deleting, setDeleting] = useState<WorkFrontStageItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const lockedReason = closed ? FRONT_CLOSED_REASON : undefined;
  const cannotUpdate = useAcao('work_front.update', 'Adicionar etapa').disabled;

  const openDialog = (stage?: WorkFrontStageItem) => {
    setEditing(stage);
    setDialogOpen(true);
  };

  const stamp = async (stage: WorkFrontStageItem, data: UpdateWorkFrontStageInput, done: string) => {
    setBusy(true);
    setError(null);
    try {
      await workFrontService.updateStage(front.id, stage.id, data);
      enqueueSnackbar(`${stage.name} ${done}.`, { variant: 'success' });
      await onChanged();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Não foi possível atualizar a etapa.');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    setDeleteError(null);
    try {
      await workFrontService.deleteStage(front.id, deleting.id);
      setDeleting(null);
      enqueueSnackbar('Etapa apagada.', { variant: 'success' });
      await onChanged();
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Não foi possível apagar a etapa.');
    } finally {
      setBusy(false);
    }
  };

  const columns: DataTableColumn<WorkFrontStageItem>[] = [
    { id: 'name', label: 'Etapa', primary: true, render: (stage) => stage.name },
    { id: 'planned', label: 'Previsto', render: (stage) => period(stage.plannedStart, stage.plannedEnd) },
    { id: 'actual', label: 'Realizado', render: (stage) => period(stage.actualStart, stage.actualEnd) },
    {
      id: 'situation',
      label: 'Situação',
      render: (stage) => (
        <Chip
          size="small"
          variant="outlined"
          label={stageSituationLabel(stage)}
          sx={readableChipSx(TONE_BY_SITUATION[stage.situation])}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Ações',
      render: (stage) => (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!stage.actualStart && (
            <LockableActionButton
              acao="work_front.update"
              rotulo={`Iniciar ${stage.name}`}
              variant="outlined"
              lockedReason={lockedReason}
              disabled={busy}
              sx={{ minHeight: TOUCH_TARGET }}
              onClick={() => void stamp(stage, { actualStart: todayIso() }, 'iniciada hoje')}
            >
              Iniciar
            </LockableActionButton>
          )}
          {stage.actualStart && !stage.actualEnd && (
            <LockableActionButton
              acao="work_front.update"
              rotulo={`Concluir ${stage.name}`}
              variant="outlined"
              lockedReason={lockedReason}
              disabled={busy}
              sx={{ minHeight: TOUCH_TARGET }}
              onClick={() => void stamp(stage, { actualEnd: todayIso() }, 'concluída hoje')}
            >
              Concluir
            </LockableActionButton>
          )}
          <LockableActionButton
            acao="work_front.update"
            rotulo={`Corrigir ${stage.name}`}
            lockedReason={lockedReason}
            disabled={busy}
            sx={{ minHeight: TOUCH_TARGET }}
            onClick={() => openDialog(stage)}
          >
            Corrigir
          </LockableActionButton>
          <LockableActionButton
            acao="work_front.update"
            rotulo={`Apagar ${stage.name}`}
            color="error"
            lockedReason={lockedReason}
            disabled={busy}
            sx={{ minHeight: TOUCH_TARGET }}
            onClick={() => {
              setDeleteError(null);
              setDeleting(stage);
            }}
          >
            Apagar
          </LockableActionButton>
        </Box>
      ),
    },
  ];

  const addAction = {
    label: 'Adicionar etapa',
    node: 'work-fronts',
    deniedHint: cannotUpdate ? 'Peça a quem administra a empresa para adicionar etapas.' : FRONT_CLOSED_REASON,
    onClick: () => openDialog(),
    allowed: !closed && !cannotUpdate,
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ typography: 'body1', color: 'text.secondary' }}>
          O prazo da frente sai das etapas: a situação dela é calculada a partir delas.
        </Box>
        <LockableActionButton
          acao="work_front.update"
          rotulo="Adicionar etapa"
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          lockedReason={lockedReason}
          sx={{ minHeight: TOUCH_TARGET, flexShrink: 0 }}
          onClick={() => openDialog()}
        >
          Adicionar etapa
        </LockableActionButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <DataTable
        embedded
        columns={columns}
        rows={front.stages}
        getRowId={(stage) => stage.id}
        emptyState={{
          title: 'Esta frente ainda não tem etapas',
          description:
            'As etapas são os passos da frente, como armação, forma e concretagem, cada uma com início e fim previstos. A situação da frente nasce delas.',
          action: addAction,
        }}
      />

      <StageDialog
        open={dialogOpen}
        frontId={front.id}
        stage={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={(saved) => {
          setDialogOpen(false);
          enqueueSnackbar(editing ? `${saved.name} corrigida.` : `${saved.name} adicionada.`, { variant: 'success' });
          void onChanged();
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Apagar etapa"
        message={deleting ? `Apagar a etapa ${deleting.name}? A situação da frente será recalculada.` : ''}
        confirmLabel="Apagar etapa"
        confirmColor="error"
        loading={busy}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
};

export default StagesTab;
